import type { AuditResult, PhasePrompt } from '@/types/plan';

export interface PhaseVerificationIssue {
  step?: string;
  severity: string;
  category?: string;
  message: string;
}

export interface PersistentInteractionVerificationFailure {
  reason: string;
  issues: string[];
}

export interface PersistentInteractionPhaseFailureOutcome {
  executionError: string;
  implementationAudit: AuditResult;
}

export function getPersistentInteractionVerificationFailure(params: {
  phase: Pick<PhasePrompt, 'id' | 'title' | 'domain'>;
  verificationIssues: PhaseVerificationIssue[];
}): PersistentInteractionVerificationFailure | null {
  const { phase, verificationIssues } = params;
  if (phase.domain !== 'interaction') return null;

  const relevantIssues = verificationIssues.filter((issue) =>
    issue.severity === 'error' &&
    (issue.step === 'Interaction' || issue.category === 'interaction'),
  );

  if (relevantIssues.length === 0) return null;

  const uniqueMessages = Array.from(new Set(relevantIssues.map(issue => issue.message.trim()).filter(Boolean)));
  const reason = `Interaction coverage verification failed after retries: ${uniqueMessages.join('; ')}`;

  return {
    reason,
    issues: uniqueMessages,
  };
}

export function buildSyntheticInteractionVerificationAudit(params: {
  phase: Pick<PhasePrompt, 'id' | 'title'>;
  failure: PersistentInteractionVerificationFailure;
}): AuditResult {
  const { phase, failure } = params;

  return {
    id: `synthetic-interaction-${phase.id}`,
    auditType: 'implementation',
    targetId: phase.id,
    verdict: 'fail',
    overallScore: 0,
    criteria: [
      {
        name: 'interaction_coverage',
        verdict: 'fail',
        score: 0,
        reasoning: failure.reason,
        feedback: [
          {
            text: 'Interaction verification found missing required behaviors after the orchestrator retry path.',
            sentiment: 'negative',
          },
          {
            text: `${failure.issues.length} deterministic interaction verification issue(s) remained unresolved.`,
            sentiment: 'neutral',
          },
        ],
        suggestions: [
          'Fix the missing interaction workflows before treating this phase as complete.',
        ],
      },
    ],
    blockingIssues: [failure.reason],
    warnings: [],
    suggestions: [
      'Re-run the interaction phase after fixing the missing onLoad, refresh, validation, or feedback behaviors.',
    ],
    auditedAt: new Date().toISOString(),
    modelUsed: 'synthetic',
    retryCount: 0,
  };
}

export function buildPersistentInteractionPhaseFailureOutcome(params: {
  phase: Pick<PhasePrompt, 'id' | 'title'>;
  failure: PersistentInteractionVerificationFailure;
}): PersistentInteractionPhaseFailureOutcome {
  const { phase, failure } = params;

  return {
    executionError: failure.reason,
    implementationAudit: buildSyntheticInteractionVerificationAudit({
      phase,
      failure,
    }),
  };
}
